/**
 * Push Notifications Service
 * 用於遠端推播（資料庫更新通知）
 * iOS 走 APNs、Android 走 FCM
 */
import { PushNotifications } from '@capacitor/push-notifications'
import type { Token, PushNotificationSchema, ActionPerformed } from '@capacitor/push-notifications'
import { Capacitor } from '@capacitor/core'

// Capacitor 權限狀態類型
export type PermissionStatus = 'granted' | 'denied' | 'prompt'

/**
 * 請求遠端推播權限
 */
export async function requestPushPermission(): Promise<PermissionStatus> {
    // Web 環境不支援
    if (!Capacitor.isNativePlatform()) {
        console.warn('遠端推播僅在原生平台支援')
        return 'denied'
    }

    try {
        const result = await PushNotifications.requestPermissions()
        // 處理 prompt-with-rationale 狀態
        if (result.receive === 'prompt-with-rationale') {
            return 'prompt'
        }
        return result.receive as PermissionStatus
    } catch (error) {
        console.error('請求遠端推播權限失敗:', error)
        return 'denied'
    }
}

/**
 * 檢查遠端推播權限狀態
 */
export async function checkPushPermission(): Promise<PermissionStatus> {
    if (!Capacitor.isNativePlatform()) {
        return 'denied'
    }

    try {
        const result = await PushNotifications.checkPermissions()
        // 處理 prompt-with-rationale 狀態
        if (result.receive === 'prompt-with-rationale') {
            return 'prompt'
        }
        return result.receive as PermissionStatus
    } catch (error) {
        console.error('檢查遠端推播權限失敗:', error)
        return 'denied'
    }
}

/**
 * 註冊遠端推播並取得 Token
 * 需要先呼叫 requestPushPermission 並獲得 granted
 */
export async function registerForPush(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
        console.warn('遠端推播僅在原生平台支援')
        return
    }

    try {
        await PushNotifications.register()
    } catch (error) {
        console.error('註冊遠端推播失敗:', error)
        throw error
    }
}

/**
 * 監聽遠端推播事件
 */
export function addPushListeners(handlers: {
    onRegistration?: (token: string) => void
    onRegistrationError?: (error: any) => void
    onPushReceived?: (notification: PushNotificationSchema) => void
    onPushActionPerformed?: (notification: PushNotificationSchema) => void
}): void {
    if (!Capacitor.isNativePlatform()) {
        return
    }

    // 註冊成功，取得 Token
    PushNotifications.addListener('registration', (token: Token) => {
        handlers.onRegistration?.(token.value)
    })

    // 註冊失敗
    PushNotifications.addListener('registrationError', (error: any) => {
        console.error('推播註冊失敗:', error)
        handlers.onRegistrationError?.(error)
    })

    // 收到推播（App 在前景時）
    PushNotifications.addListener('pushNotificationReceived', (notification: PushNotificationSchema) => {
        handlers.onPushReceived?.(notification)
    })

    // 使用者點擊推播
    PushNotifications.addListener('pushNotificationActionPerformed', (action: ActionPerformed) => {
        handlers.onPushActionPerformed?.(action.notification)
    })
}

/**
 * 移除所有推播監聽器
 */
export async function removeAllPushListeners(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
        return
    }

    try {
        await PushNotifications.removeAllListeners()
    } catch (error) {
        console.error('移除推播監聽器失敗:', error)
    }
}

/**
 * 取得已送達的推播通知列表
 */
export async function getDeliveredNotifications(): Promise<PushNotificationSchema[]> {
    if (!Capacitor.isNativePlatform()) {
        return []
    }

    try {
        const result = await PushNotifications.getDeliveredNotifications()
        return result.notifications
    } catch (error) {
        console.error('取得已送達通知失敗:', error)
        return []
    }
}

/**
 * 清除所有已送達的推播通知
 */
export async function removeAllDeliveredNotifications(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
        return
    }

    try {
        await PushNotifications.removeAllDeliveredNotifications()
    } catch (error) {
        console.error('清除已送達通知失敗:', error)
    }
}

// ==================== 家庭更新通知處理 ====================

type FamilyUpdateHandler = () => void | Promise<void>
let _familyUpdateHandler: FamilyUpdateHandler | null = null

/**
 * 註冊家庭資料更新的回呼（由 useAppStore 呼叫）
 * 收到 type === 'family_update' 的推播時觸發
 */
export function registerFamilyUpdateHandler(handler: FamilyUpdateHandler): void {
    _familyUpdateHandler = handler
}

/**
 * 清除家庭資料更新回呼
 */
export function unregisterFamilyUpdateHandler(): void {
    _familyUpdateHandler = null
}

/**
 * 處理家庭更新通知（前景接收或點擊後觸發）
 */
async function handleFamilyUpdateNotification(): Promise<void> {
    if (_familyUpdateHandler) {
        await _familyUpdateHandler()
    }
}

/**
 * 供其他模組（如 useAuthStore）主動觸發家庭資料重載
 */
export async function triggerFamilyUpdate(): Promise<void> {
    await handleFamilyUpdateNotification()
}

/**
 * 初始化遠端推播
 * 在 App 啟動時呼叫
 */
export async function initializePushNotifications(
    onTokenReceived?: (token: string) => void
): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
        console.warn('遠端推播僅在原生平台支援，跳過初始化')
        return
    }

    // 設定監聽器
    addPushListeners({
        onRegistration: (token) => {
            onTokenReceived?.(token)
        },
        onRegistrationError: (error) => {
            console.error('推播註冊失敗:', error)
        },
        onPushReceived: (notification) => {
            // App 在前景時收到推播
            const type = notification.data?.type
            if (
                type === 'family_update' ||
                type === 'family_join_request' ||
                type === 'family_join_approved'
            ) {
                handleFamilyUpdateNotification()
            }
            console.log('收到遠端推播:', notification)
        },
        onPushActionPerformed: (notification) => {
            // 使用者點擊推播開啟 App
            const type = notification.data?.type
            if (
                type === 'family_update' ||
                type === 'family_join_request' ||
                type === 'family_join_approved'
            ) {
                handleFamilyUpdateNotification()
            }
            console.log('使用者點擊推播:', notification)
        },
    })

    // 檢查權限
    const permission = await checkPushPermission()
    if (permission === 'granted') {
        // 已有權限，直接註冊
        await registerForPush()
    }
}
